import { FC, memo, useCallback, useEffect, useRef } from "react";
import { BoundingBoxMatch } from "../../hooks/useTextBoundingBoxes";

type TextHighlightOverlayProps = {
  matches: BoundingBoxMatch[];
  rotation: number;
  transform?: string;
  transformOrigin?: string;
};

const CANVAS_SIZE = 1000;

const GRADIENT_STOPS: Array<[number, string]> = [
  [0, "rgba(255, 200, 0, 0.3)"],
  [0.3, "rgba(255, 180, 0, 0.25)"],
  [0.6, "rgba(255, 100, 0, 0.01)"],
  [1, "rgba(255, 50, 0, 0)"],
];

const RADIUS_MULTIPLIER = 1;

function transformBoxForRotation(
  box: [number, number, number, number],
  rotation: number
): [number, number, number, number] {
  const [yMin, xMin, yMax, xMax] = box;
  const normalizedRotation = ((rotation % 360) + 360) % 360;

  switch (normalizedRotation) {
    case 90:
      return [xMin, CANVAS_SIZE - yMax, xMax, CANVAS_SIZE - yMin];
    case 180:
      return [
        CANVAS_SIZE - yMax,
        CANVAS_SIZE - xMax,
        CANVAS_SIZE - yMin,
        CANVAS_SIZE - xMin,
      ];
    case 270:
      return [CANVAS_SIZE - xMax, yMin, CANVAS_SIZE - xMin, yMax];
    default:
      return [yMin, xMin, yMax, xMax];
  }
}

const TextHighlightOverlayComponent: FC<TextHighlightOverlayProps> = ({
  matches,
  rotation,
  transform,
  transformOrigin,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const renderHeatmap = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.globalCompositeOperation = "lighter";

    matches.forEach((match) => {
      const [yMin, xMin, yMax, xMax] = transformBoxForRotation(
        match.box_2d,
        rotation
      );

      const left = xMin;
      const top = yMin;
      const width = xMax - xMin;
      const height = yMax - yMin;

      const centerX = left + width / 2;
      const centerY = top + height / 2;
      const radius = Math.max(width, height) * RADIUS_MULTIPLIER;

      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        radius
      );

      GRADIENT_STOPS.forEach(([offset, color]) => {
        gradient.addColorStop(offset, color);
      });

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    });

    ctx.globalCompositeOperation = "source-over";
  }, [matches, rotation]);

  useEffect(() => {
    renderHeatmap();
  }, [renderHeatmap]);

  if (matches.length === 0) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_SIZE}
      height={CANVAS_SIZE}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{
        transform,
        transformOrigin,
      }}
    />
  );
};

export const TextHighlightOverlay = memo(TextHighlightOverlayComponent);
TextHighlightOverlay.displayName = "TextHighlightOverlay";
